import { Children, useState } from "react";
import { ArrowLeft, ArrowRight, Lock, Plus, Trash2, X } from "lucide-react";
import { Select, Toggle, inputClass, textareaClass } from "./primitives";
import { placeholderNumbers } from "./templates/templateFormat";
import { replyButtons } from "./StageTemplatePicker";
import { useAddTemplateDialog } from "./templates/AddExistingTemplate";
import Dropdown from "../../components/Dropdown";

// The parts of one Service journey stage, laid out as a settings page: a flow summary, then
// Trigger, Message, Replies, Capture and More settings. Stages the brand added edit every
// part; built-in stages edit what their code supports and show the rest as fixed.

// One way to start each stage, so the flow is the list order: Automatic follows the stage above.
export const TRIGGERS = [
  ["automatic", "Automatic"],
  ["manual", "Manual"],
  ["group_created", "Group created"],
  ["customer_joined", "Customer joined"],
];
const ACTIONS = [
  ["done", "Mark done"],
  ["status", "Set status"],
  ["reply", "Send reply"],
  ["escalate", "Escalate"],
  ["rating:experience", "Save rating"],
  ["rating:recommend", "Save NPS"],
];
// "Save rating" (experience, 1-5) and "Save NPS" (0-10) are one saved action with its kind.
const actionOf = (r) => (r.action === "rating" ? `rating:${r.rating || "experience"}` : r.action);
const fromAction = (v) => (v.startsWith("rating:") ? { action: "rating", rating: v.slice(7) } : { action: v });
const ESCALATIONS = [
  ["predelivery_feedback", "Pre-delivery"],
  ["postdelivery_feedback", "Post-delivery"],
  ["complaint", "Complaint"],
  ["issue", "Issue"],
];
// The booking form's files a stage can send when the booking has one.
const BOOKING_FILES = [
  ["inventory", "Inventory photos"],
  ["estimate", "Estimate"],
  ["job_card", "Job card"],
];
// A template's file, from its type on the Templates tab.
const TEMPLATE_FILE = { document: "document", image: "media", video: "media" };
// The file a template carries, by its type on the Templates tab: what the Attachment choice means.
const FILE_OF_TEMPLATE = { document: "document", image: "image", video: "video" };
const FILE_CHOICES = [
  ["none", "None"],
  ["document", "Document"],
  ["image", "Photo"],
  ["video", "Video"],
];
const ATTACHMENT_OF_FILE = { none: "none", document: "document", image: "media", video: "media" };
const FILE_WORDS = { none: "None", document: "Document", media: "Photo or video", album: "Photos and videos" };
const DELIVERY_OPTIONS = [
  ["Pickup/Drop", "Pick and drop"],
  ["Self Visit", "Self visit"],
];

export const DEFAULT_BLOCKS = {
  trigger: "automatic",
  message_type: "template",
  text: "",
  delay_seconds: 0,
  recipient: "group",
  attachment: "none",
  booking_file: "",
  only_if: {},
  delete_group_minutes: null,
  capture_rules: [],
  keyword_sent_by: "advisor",
  keyword_match: "contains",
};

const UNITS = [
  ["sec", 1],
  ["min", 60],
  ["hours", 3600],
  ["days", 86400],
];
const unitOf = (s) => (s > 0 && s % 86400 === 0 ? "days" : s > 0 && s % 3600 === 0 ? "hours" : s > 0 && s % 60 === 0 ? "min" : s > 0 ? "sec" : "min");
const per = (u) => UNITS.find(([k]) => k === u)[1];

// ---------------------------------------------------------------- building blocks

export function Segmented({ options, value, onChange, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-[10px] bg-[#F2F4F7] p-[3px]">
      {options.map(([key, word]) => {
        const on = value === key;
        return (
          <button
            key={key || "none"}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(key)}
            className={`h-8 cursor-pointer rounded-[8px] px-3 text-[13px] font-medium transition-[background,color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6] ${
              on ? "bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.10),0_1px_3px_rgba(16,24,40,0.06)]" : "text-[#475467] hover:text-[#101828]"
            }`}
          >
            {word}
          </button>
        );
      })}
    </div>
  );
}

function Row({ label, children, align = "center" }) {
  return (
    <div className={`grid gap-2 sm:grid-cols-[148px_minmax(0,1fr)] sm:gap-6 ${align === "start" ? "sm:items-start" : "sm:items-center"}`}>
      <div className={`text-[13.5px] font-medium text-[#344054] ${align === "start" ? "sm:pt-[9px]" : ""}`}>{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// A value the stage's code fixes: plain text with a lock, never shaped like an input.
function Fixed({ children }) {
  return (
    <div className="inline-flex min-h-[40px] items-center gap-2 text-[14px] text-[#344054]">
      <Lock size={13} className="flex-none text-[#98A2B3]" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

// One step of a stage. Its title is the step's name in StageSteps; `aside` holds the step's own action.
// eslint-disable-next-line no-unused-vars
function Section({ title, aside, children }) {
  return (
    <section className="flex flex-col gap-5">
      {aside && <div className="-mb-1 flex justify-end">{aside}</div>}
      {children}
    </section>
  );
}

// A stage's parts, one at a time, the way a template is made: Trigger, then Message, and so on.
// Every step can be opened directly; Back and Next walk through them.
export function StageSteps({ children }) {
  const items = Children.toArray(children).filter(Boolean);
  const [at, setAt] = useState(0);
  const i = Math.min(at, items.length - 1);
  const next = items[i + 1];
  return (
    <>
      <nav aria-label="Stage settings" className="mb-6">
        <ol className="m-0 inline-flex list-none flex-wrap gap-[2px] rounded-[10px] bg-[#F2F4F7] p-[3px]">
          {items.map((item, n) => {
            const active = n === i;
            return (
              <li key={item.props.title}>
                <button
                  type="button"
                  onClick={() => setAt(n)}
                  aria-current={active ? "step" : undefined}
                  className={`flex h-8 cursor-pointer items-center gap-[6px] whitespace-nowrap rounded-[8px] pl-2 pr-3 text-[13px] font-medium transition-[background,color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6] ${
                    active ? "bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.10),0_1px_3px_rgba(16,24,40,0.06)]" : "text-[#667085] hover:text-[#101828]"
                  }`}
                >
                  <span
                    className={`flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10.5px] font-semibold tabular-nums ${active ? "bg-[#2667B5] text-white" : "bg-white text-[#667085] ring-1 ring-[#D0D5DD]"}`}
                  >
                    {n + 1}
                  </span>
                  {item.props.title}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
      <div className="min-h-[220px]">{items[i]}</div>
      {items.length > 1 && (
        <div className="mt-8 flex items-center justify-between gap-3 border-t border-[#EAECF0] pt-5">
          {i > 0 ? (
            <button type="button" onClick={() => setAt(i - 1)} className="inline-flex h-9 cursor-pointer items-center gap-[6px] rounded-lg px-3 text-[13.5px] font-medium text-[#475467] transition-colors duration-150 hover:bg-[#F2F4F7] hover:text-[#101828]">
              <ArrowLeft size={15} aria-hidden="true" /> {items[i - 1].props.title}
            </button>
          ) : (
            <span />
          )}
          {next && (
            <button
              type="button"
              onClick={() => setAt(i + 1)}
              className="inline-flex h-9 cursor-pointer items-center gap-[6px] rounded-lg border border-[#D0D5DD] bg-white px-4 text-[13.5px] font-semibold text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors duration-150 hover:bg-[#F9FAFB] hover:text-[#101828]"
            >
              Next: {next.props.title} <ArrowRight size={15} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </>
  );
}

function DelayInput({ seconds, onChange, max = 7 * 86400, units = UNITS }) {
  const unit = unitOf(seconds);
  const shown = units.some(([k]) => k === unit) ? unit : units[0][0];
  return (
    <div className="flex gap-2">
      <input
        type="number"
        min={0}
        className={`${inputClass} !w-[96px] tabular-nums`}
        value={Math.round((seconds || 0) / per(shown))}
        onChange={(e) => onChange(Math.min(max, Math.max(0, Number(e.target.value) || 0) * per(shown)))}
        aria-label="Delay"
      />
      <Select aria-label="Delay unit" value={shown} onChange={(e) => onChange(Math.min(max, Math.round((seconds || 0) / per(shown)) * per(e.target.value)))} className="!w-[112px]">
        {units.map(([k]) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </Select>
    </div>
  );
}

// The rules as a sheet: one row per rule, read left to right, "Who does what, then what".
// Cells hold borderless controls, so the rules read like a spreadsheet, not a stack of forms.
const RULE_COLS = "md:grid-cols-[110px_132px_minmax(0,1.1fr)_128px_minmax(0,1.3fr)_40px]";
const CELL = "flex min-h-[44px] min-w-0 items-center md:border-l md:border-[#EAECF0] md:first:border-l-0";
const CELL_TEXT = "px-3 text-[14px] text-[#344054]";
const CELL_HEAD = "px-3 py-2 text-[12px] font-semibold text-[#667085]";

function CellSelect({ label, value, onChange, children, disabled }) {
  return (
    <div className="relative w-full">
      <Dropdown
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full cursor-pointer appearance-none truncate border-0 bg-transparent pl-3 pr-3 text-[14px] text-[#101828] outline-none transition-colors duration-150 hover:bg-[#F9FAFB] focus:bg-[#EFF5FC] disabled:cursor-default disabled:hover:bg-transparent"
      >
        {children}
      </Dropdown>
    </div>
  );
}

function CellInput({ label, value, onChange, placeholder, type = "text", min, max, className = "" }) {
  return (
    <input
      type={type}
      min={min}
      max={max}
      aria-label={label}
      value={value}
      placeholder={placeholder}
      maxLength={type === "text" ? 40 : undefined}
      onChange={(e) => onChange(e.target.value)}
      className={`h-11 w-full min-w-0 border-0 bg-transparent px-3 text-[14px] text-[#101828] outline-none transition-colors duration-150 placeholder:text-[#98A2B3] hover:bg-[#F9FAFB] focus:bg-[#EFF5FC] ${className}`}
    />
  );
}

// Words as small chips inside the cell; Enter or comma adds one.
function CellWords({ words, onChange }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const w = draft.trim().replace(/,$/, "").toLowerCase();
    if (w && !words.includes(w)) onChange([...words, w]);
    setDraft("");
  };
  return (
    <div className="flex min-h-[44px] w-full flex-wrap items-center gap-1 px-2 py-[6px] transition-colors duration-150 focus-within:bg-[#EFF5FC] hover:bg-[#F9FAFB]">
      {words.map((w) => (
        <span key={w} className="inline-flex h-6 max-w-full items-center gap-[2px] rounded-md bg-[#EEF4FF] pl-2 pr-[2px] text-[13px] text-[#1F5596]">
          <span className="truncate whitespace-nowrap">{w}</span>
          <button
            type="button"
            onClick={() => onChange(words.filter((x) => x !== w))}
            aria-label={`Remove ${w}`}
            className="flex h-5 w-5 flex-none cursor-pointer items-center justify-center rounded text-[#5B7DB1] hover:bg-[#DCE7FB] hover:text-[#1F5596]"
          >
            <X size={11} aria-hidden="true" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => (e.target.value.endsWith(",") ? commit() : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && !draft && words.length) {
            onChange(words.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={words.length ? "" : "Type words, press Enter"}
        aria-label="Words"
        className="h-7 min-w-[64px] flex-1 border-0 bg-transparent px-1 text-[14px] text-[#101828] outline-none placeholder:text-[#98A2B3]"
      />
    </div>
  );
}

export function RulesTable({ children, empty, onAdd }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#EAECF0] bg-white">
      <div className={`hidden bg-[#F9FAFB] md:grid ${RULE_COLS}`}>
        {["Who", "Does", "Button or words", "Then", "Details", ""].map((h, i) => (
          <span key={i} className={`${CELL_HEAD} ${i ? "border-l border-[#EAECF0]" : ""}`}>
            {h}
          </span>
        ))}
      </div>
      {empty && <p className="m-0 border-t border-[#EAECF0] px-3 py-3 text-[14px] text-[#667085]">No rules</p>}
      {children}
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="flex h-11 w-full cursor-pointer items-center gap-[6px] border-0 border-t border-[#EAECF0] bg-transparent px-3 text-left text-[14px] font-medium text-[#2667B5] transition-colors duration-150 hover:bg-[#F5F9FF] focus-visible:outline-none focus-visible:bg-[#EFF5FC]"
        >
          <Plus size={15} aria-hidden="true" /> Add rule
        </button>
      )}
    </div>
  );
}

function RuleDetails({ rule, onChange, approved }) {
  if (rule.action === "reply") {
    return (
      <CellSelect label="Reply message" value={rule.reply_template_id ?? ""} onChange={(v) => onChange({ reply_template_id: v ? Number(v) : null })}>
        <option value="">Select template</option>
        {approved.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </CellSelect>
    );
  }
  if (rule.action === "escalate") {
    return (
      <CellSelect label="Escalation" value={rule.escalation || ""} onChange={(escalation) => onChange({ escalation })}>
        <option value="">Select escalation</option>
        {ESCALATIONS.map(([k, label]) => (
          <option key={k} value={k}>
            {label}
          </option>
        ))}
      </CellSelect>
    );
  }
  if (rule.action === "rating") {
    const [low, high] = rule.rating === "recommend" ? [0, 10] : [1, 5];
    return (
      <CellInput
        type="number"
        min={low}
        max={high}
        label="Score"
        placeholder={`Score ${low}-${high}`}
        value={rule.score ?? ""}
        onChange={(v) => onChange({ score: v === "" ? null : Number(v) })}
        className="tabular-nums"
      />
    );
  }
  return (
    <CellInput
      label="Status"
      value={rule.status || ""}
      placeholder={rule.action === "status" ? "Status, e.g. Washed" : "Status (optional)"}
      onChange={(status) => onChange({ status })}
    />
  );
}

// One rule row. Who and Does map onto the saved rule: a customer's tap is a button rule; a
// written message is a word rule with whose words count and how they match.
export function RuleRow({ rule, onChange, onRemove, buttons = [], allowButton = true, lockThen = false, approved = [] }) {
  const who = rule.source === "button" ? "customer" : rule.sent_by || "advisor";
  const does = rule.source === "button" ? "button" : rule.match === "exact" ? "exact" : "contains";
  const buttonChoices = [...new Set([...buttons, ...(rule.button ? [rule.button] : [])])];
  const words = (w, d) => ({ source: "keyword", sent_by: w, match: d === "exact" ? "exact" : "contains", keywords: rule.keywords || [] });
  const setWho = (w) => onChange(w === "customer" && allowButton ? { source: "button", button: rule.button || "" } : words(w, does === "button" ? "contains" : does));
  const setDoes = (d) => onChange(d === "button" ? { source: "button", button: rule.button || "" } : words(who, d));
  return (
    <div className={`grid grid-cols-1 border-t border-[#EAECF0] ${RULE_COLS}`}>
      <div className={CELL}>
        <CellSelect label="Who" value={who} onChange={setWho}>
          <option value="customer">Customer</option>
          <option value="advisor">Advisor</option>
          <option value="staff">Any staff</option>
        </CellSelect>
      </div>
      <div className={CELL}>
        <CellSelect label="Does" value={does} onChange={setDoes}>
          {who === "customer" && (
            <option value="button" disabled={!allowButton}>
              {allowButton ? "Taps button" : "Taps button (engine off)"}
            </option>
          )}
          <option value="contains">Writes</option>
          <option value="exact">Writes exactly</option>
        </CellSelect>
      </div>
      <div className={CELL}>
        {does === "button" ? (
          <CellSelect label="Button" value={rule.button || ""} onChange={(button) => onChange({ button })}>
            <option value="">{buttonChoices.length ? "Select button" : "No buttons in the template"}</option>
            {buttonChoices.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </CellSelect>
        ) : (
          <CellWords words={rule.keywords || []} onChange={(keywords) => onChange({ keywords })} />
        )}
      </div>
      <div className={CELL}>
        {lockThen ? (
          <span className={CELL_TEXT}>Mark done</span>
        ) : (
          <CellSelect label="Then" value={actionOf(rule)} onChange={(v) => onChange(fromAction(v))}>
            {ACTIONS.map(([k, w]) => (
              <option key={k} value={k}>
                {w}
              </option>
            ))}
          </CellSelect>
        )}
      </div>
      <div className={CELL}>
        {lockThen ? <span className={`${CELL_TEXT} text-[#98A2B3]`}>—</span> : <RuleDetails rule={rule} onChange={onChange} approved={approved} />}
      </div>
      <div className={`${CELL} justify-center`}>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove rule"
          title="Remove rule"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[#98A2B3] transition-colors duration-150 hover:bg-[#FEF3F2] hover:text-[#D92D20]"
        >
          <Trash2 size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// A rule a built-in stage's older code applies (brands not on the stage engine), shown in the
// same columns, fixed.
function FixedRule({ buttons, then, details }) {
  return (
    <div className={`grid grid-cols-1 border-t border-[#EAECF0] bg-[#FCFCFD] ${RULE_COLS}`}>
      <div className={CELL}>
        <span className={CELL_TEXT}>Customer</span>
      </div>
      <div className={CELL}>
        <span className={CELL_TEXT}>Taps button</span>
      </div>
      <div className={`${CELL} flex-wrap gap-1 px-2 py-[6px]`}>
        {buttons.map((l) => (
          <span key={l} className="inline-flex h-6 items-center rounded-md bg-[#EEF4FF] px-2 text-[13px] text-[#1F5596]">
            {l}
          </span>
        ))}
      </div>
      <div className={CELL}>
        <span className={CELL_TEXT}>{then}</span>
      </div>
      <div className={CELL}>
        <span className={CELL_TEXT}>{details}</span>
      </div>
      <div className={`${CELL} justify-center`}>
        <Lock size={13} className="text-[#98A2B3]" aria-label="Built in" />
      </div>
    </div>
  );
}

const NEW_RULE = { source: "keyword", button: "", keywords: [], sent_by: "advisor", match: "contains", action: "done", status: "", stage: "", reply_template_id: null };

const templateOption = (t) => `${t.name}${t.status === "pending" ? " · Pending" : ""}`;

// The stage's own template, picked from the Templates tab, with a field for each {{n}}.
function TemplatePick({ templates, fields, pick, onPick, allowManual, manualName, onManualName }) {
  const [manual, setManual] = useState(!pick && !!manualName);
  const chosen = templates.find((t) => t.id === pick?.id);
  const addTemplate = useAddTemplateDialog();
  const value = pick?.id ? String(pick.id) : manual ? "__manual" : "";
  return (
    <>
      <Row label="Template">
        <Select
          aria-label="Template"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__manual") {
              setManual(true);
              onPick(null);
            } else {
              setManual(false);
              const t = templates.find((x) => x.id === Number(v));
              onPick(v ? { id: Number(v), field_map: { ...(t?.field_map || {}) } } : null);
            }
          }}
          className="w-full max-w-[440px]"
        >
          <option value="">{templates.length ? "Select template" : "No approved templates"}</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {templateOption(t)}
            </option>
          ))}
          {allowManual && <option value="__manual">Enter template name</option>}
        </Select>
      </Row>
      {addTemplate && (
        <Row label="">
          <button
            type="button"
            onClick={() => addTemplate((row) => row && (row.status === "approved" || row.status === "pending") && onPick({ id: row.id, field_map: { ...(row.field_map || {}) } }))}
            className="cursor-pointer border-0 bg-transparent p-0 text-left text-[13.5px] font-medium text-[#2667B5] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6]"
          >
            {templates.length ? "Not in the list? Add a template from Chat360" : "Add a template you already made on Chat360"}
          </button>
        </Row>
      )}
      {allowManual && manual && !pick && (
        <Row label="Template name">
          <input className={`${inputClass} max-w-[440px]`} value={manualName || ""} onChange={(e) => onManualName(e.target.value)} aria-label="Template name" />
        </Row>
      )}
      {chosen && !placeholderNumbers(chosen.body).every((n) => (chosen.field_map || {})[n]) && (
        <Row label="">
          <p className="m-0 text-[13px] text-[#B54708]">Set this template's fields on the Templates tab.</p>
        </Row>
      )}
    </>
  );
}

// ---------------------------------------------------------------- header and flow

export function StageHeader({ row, index, total, onRename, onVisible, onDelete, error }) {
  return (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2 text-[12.5px] text-[#667085]">
            <span className="tabular-nums">
              Stage {index + 1} of {total}
            </span>
            <span aria-hidden="true">·</span>
            <span className={`rounded-full px-2 py-[1px] text-[11.5px] font-medium ${row.is_custom ? "bg-[#EFF5FC] text-[#1F5596]" : "bg-[#F2F4F7] text-[#475467]"}`}>
              {row.is_custom ? "Custom" : "Built-in"}
            </span>
          </div>
          <input
            value={row.label}
            onChange={(e) => onRename(e.target.value)}
            aria-label="Stage name"
            className="-ml-2 w-full max-w-[520px] rounded-lg border border-transparent bg-transparent px-2 py-1 text-[22px] font-semibold tracking-[-0.01em] text-[#101828] outline-none transition-[border-color,box-shadow] duration-150 hover:border-[#EAECF0] focus:border-[#2667B5] focus:shadow-[0_0_0_3px_rgba(38,103,181,0.15)]"
          />
          {error && <div className="mt-1 text-[12.5px] text-[#D92D20]">{error}</div>}
        </div>
        <div className="flex flex-none items-center gap-3 pt-5">
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Delete stage"
              title="Delete stage"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-[#667085] transition-colors duration-150 hover:bg-[#FEF3F2] hover:text-[#D92D20] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FDA29B]"
            >
              <Trash2 size={16} aria-hidden="true" />
            </button>
          )}
          <label className="flex items-center gap-2 text-[13.5px] font-medium text-[#344054]">
            Visible
            <Toggle checked={row.is_visible} onChange={onVisible} label="Visible" />
          </label>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- a stage the brand added

// One form for every stage. A stage the brand added edits everything; a built-in stage is the same
// form filled in with what it does today, and `fixed` says which parts its code decides:
//   fixed.trigger      text shown instead of the trigger choice (null: choose; see triggerOptions)
//   fixed.triggerOptions  the triggers a built-in stage can switch between
//   fixed.delay        {seconds, onChange, max, units} when the stage has its own wait
//   fixed.message      the stage's own message settings, in place of the template choice
//   fixed.recipient    text, or a node to choose with
//   fixed.attachment   text, or a node to choose with (set fixed.choose, so it shows without a lock)
//   fixed.replies      [[button, status, reply, next]] rows its code applies
//   fixed.oneRule      capture is one rule whose result is always Mark done
//   fixed.more         node for the stage's own extra settings
export function StageForm({ row, templates, fields, pick, onPick, onChange, fixed = null }) {
  const b = { ...DEFAULT_BLOCKS, ...(row.blocks || {}) };
  const set = (patch) => onChange({ blocks: { ...b, ...patch } });
  const rules0 = b.capture_rules || [];
  const template = templates.find((t) => t.id === pick?.id);
  const approved = templates.filter((t) => t.status === "approved");
  const buttons = [...new Set([...replyButtons(template), ...rules0.filter((r) => r.source === "button").map((r) => r.button).filter(Boolean)])];
  const rules = rules0;
  const setRule = (i, patch) => set({ capture_rules: rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const triggerOptions = fixed?.triggerOptions || TRIGGERS;
  // A template message carries what its template takes; a text can carry photos and videos.
  const textMessage = b.message_type === "text";
  const attachment = textMessage ? (b.attachment === "album" ? "album" : "none") : template ? TEMPLATE_FILE[template.template_type] || "none" : b.attachment;
  // A template message's file is chosen here, and the template list shows only that kind.
  const pickedFile = template ? FILE_OF_TEMPLATE[template.template_type] || "none" : "";
  const fileChoice = textMessage ? "" : b.file_type || pickedFile || (b.attachment === "document" ? "document" : "");
  const choices = fileChoice ? templates.filter((t) => (FILE_OF_TEMPLATE[t.template_type] || "none") === fileChoice) : templates;
  const chooseFile = (file_type) => {
    set(file_type ? { file_type, attachment: ATTACHMENT_OF_FILE[file_type], booking_file: file_type === "none" ? "" : b.booking_file } : { file_type: "" });
    if (file_type && template && pickedFile !== file_type) onPick(null);
  };
  const canAddRule = fixed?.noCapture ? false : fixed?.oneRule ? rules.length === 0 : true;
  const fixedRows = (fixed?.replies || []).flatMap(([button, status, , next]) => [
    ...(status !== "—" ? [[button, "Set status", status]] : []),
    ...(next !== "Stay" ? [[button, "Starts", next]] : []),
  ]);


  return (
    <StageSteps>
      <Section title="Trigger">
        <Row label="Starts on">
          {fixed?.trigger ? (
            <Fixed>{fixed.trigger}</Fixed>
          ) : (
            <Select
              aria-label="Starts on"
              value={b.trigger}
              onChange={(e) => {
                const trigger = e.target.value;
                set({ trigger });
              }}
              className="w-full max-w-[320px]"
            >
              {triggerOptions.map(([k, w]) => (
                <option key={k} value={k}>
                  {w}
                </option>
              ))}
            </Select>
          )}
        </Row>
        {fixed?.delay ? (
          <Row label="Delay">
            <DelayInput seconds={fixed.delay.seconds} max={fixed.delay.max} units={fixed.delay.units} onChange={fixed.delay.onChange} />
          </Row>
        ) : (
          !fixed &&
          b.trigger !== "manual" && (
            <Row label="Delay">
              <DelayInput seconds={b.delay_seconds} onChange={(delay_seconds) => set({ delay_seconds })} />
            </Row>
          )
        )}
      </Section>

      <Section title="Message">
        {fixed ? (
          fixed.message || (
            <Row label="Template">
              <Fixed>None</Fixed>
            </Row>
          )
        ) : (
          <>
            <Row label="Type">
              <Segmented
                label="Message type"
                options={[["template", "Template"], ["text", "Text"]]}
                value={b.message_type === "text" ? "text" : "template"}
                onChange={(message_type) => set({ message_type, recipient: message_type === "text" ? "group" : b.recipient })}
              />
            </Row>
            {textMessage ? (
              <Row label="Text" align="start">
                <textarea
                  className={`${textareaClass} min-h-[120px] max-w-[560px]`}
                  value={b.text || ""}
                  placeholder="Hello {{customer_name}}, your {{reg_no}} is ready."
                  onChange={(e) => set({ text: e.target.value })}
                  aria-label="Message text"
                />
              </Row>
            ) : (
              <TemplatePick
                templates={choices}
                fields={fields}
                pick={pick}
                onPick={(p) => {
                  onPick(p);
                  const t = templates.find((x) => x.id === p?.id);
                  set({ attachment: TEMPLATE_FILE[t?.template_type] || "none", file_type: FILE_OF_TEMPLATE[t?.template_type] || "none" });
                }}
                allowManual={false}
              />
            )}
          </>
        )}
        <Row label="Send to">
          {fixed ? (
            typeof fixed.recipient === "string" || !fixed.recipient ? <Fixed>{fixed.recipient || "Group"}</Fixed> : fixed.recipient
          ) : (
            b.message_type === "text" ? (
              <Fixed>Group</Fixed>
            ) : (
              <Segmented label="Send to" options={[["group", "Group"], ["customer", "Customer only"]]} value={b.recipient} onChange={(recipient) => set({ recipient })} />
            )
          )}
        </Row>
        <Row label="Attachment">
          {fixed?.choose ? (
            fixed.attachment
          ) : fixed ? (
            <Fixed>{fixed.attachment || "None"}</Fixed>
          ) : textMessage ? (
            <Segmented
              label="Attachment"
              options={[["none", "None"], ["album", "Photos and videos"]]}
              value={attachment}
              onChange={(v) => set({ attachment: v, booking_file: v === "none" ? "" : b.booking_file })}
            />
          ) : (
            <Select aria-label="Attachment" value={fileChoice} onChange={(e) => chooseFile(e.target.value)} className="w-full max-w-[320px]">
              <option value="">Not chosen</option>
              {FILE_CHOICES.map(([k, word]) => (
                <option key={k} value={k}>
                  {word}
                </option>
              ))}
            </Select>
          )}
        </Row>
        {!fixed && attachment !== "none" && (
          <Row label="Booking form file">
            <Select aria-label="Booking form file" value={b.booking_file || ""} onChange={(e) => set({ booking_file: e.target.value })} className="w-full max-w-[320px]">
              <option value="">None</option>
              {BOOKING_FILES.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </Row>
        )}
      </Section>

      <Section title="Capture">
        <RulesTable
          empty={rules.length === 0 && !fixedRows.length}
          onAdd={canAddRule ? () => set({ capture_rules: [...rules, { ...NEW_RULE, source: buttons.length && !fixed ? "button" : "keyword" }] }) : null}
        >
          {fixedRows.map(([button, then, details], i) => (
            <FixedRule key={i} buttons={button.split(", ")} then={then} details={details} />
          ))}
          {rules.map((r, i) => (
            <RuleRow
              key={i}
              rule={r}
              buttons={buttons}
              allowButton={!fixed}
              lockThen={!!fixed?.oneRule}
              approved={approved}
              onChange={(patch) => setRule(i, patch)}
              onRemove={() => set({ capture_rules: rules.filter((_, j) => j !== i) })}
            />
          ))}
        </RulesTable>
      </Section>

      {fixed ? (
        fixed.more && <Section title="More settings">{fixed.more}</Section>
      ) : (
        <Section title="More settings">
          <Row label="Only send if">
            <div className="flex flex-wrap gap-2">
              <Select
                aria-label="Only send if"
                value={b.only_if?.field || ""}
                onChange={(e) => set({ only_if: e.target.value ? { field: e.target.value, value: "" } : {} })}
                className="!w-[200px]"
              >
                <option value="">Always send</option>
                <option value="delivery_option">Delivery option is</option>
                <option value="service_type">Service type is</option>
              </Select>
              {b.only_if?.field === "delivery_option" && (
                <Select aria-label="Delivery option" value={b.only_if.value || ""} onChange={(e) => set({ only_if: { ...b.only_if, value: e.target.value } })} className="!w-[200px]">
                  <option value="">Select option</option>
                  {DELIVERY_OPTIONS.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
              {b.only_if?.field === "service_type" && (
                <input
                  className={`${inputClass} !w-[200px]`}
                  value={b.only_if.value || ""}
                  maxLength={40}
                  placeholder="Paid service"
                  onChange={(e) => set({ only_if: { ...b.only_if, value: e.target.value } })}
                  aria-label="Service type"
                />
              )}
            </div>
          </Row>
          <Row label="Delete group">
            <div className="flex flex-wrap items-center gap-3">
              <Toggle
                checked={b.delete_group_minutes != null}
                onChange={(on) => set({ delete_group_minutes: on ? 1440 : null })}
                label="Delete the group after this stage"
              />
              {b.delete_group_minutes != null && (
                <DelayInput
                  seconds={b.delete_group_minutes * 60}
                  max={90 * 86400}
                  units={UNITS.filter(([k]) => k !== "sec")}
                  onChange={(seconds) => set({ delete_group_minutes: Math.round(seconds / 60) })}
                />
              )}
            </div>
          </Row>
        </Section>
      )}
    </StageSteps>
  );
}

// ---------------------------------------------------------------- a built-in stage

// One message a built-in stage sends: on/off, template or text, and the template itself.
export function BuiltInMessage({ tpl, showName, templates, fields, pick, onPick, onPatch, lock, error }) {
  const direct = tpl.send_mode === "direct";
  const enabled = lock.locked ? false : tpl.enabled == null ? tpl.default_enabled : tpl.enabled;
  return (
    <div className="flex flex-col gap-5">
      <Row label={showName ? tpl.label : "Active"}>
        <div className="flex items-center gap-3">
          <Toggle checked={!!enabled} onChange={(on) => onPatch({ enabled: on })} label={`${tpl.label} active`} disabled={lock.locked} />
          <span className="text-[13px] text-[#667085]">{lock.locked ? lock.reason : enabled ? "On" : "Off"}</span>
        </div>
      </Row>
      {tpl.allows_direct && (
        <Row label="Type">
          <Segmented label="Message type" options={[["template", "Template"], ["direct", "Text"]]} value={direct ? "direct" : "template"} onChange={(v) => onPatch({ send_mode: v })} />
        </Row>
      )}
      {direct ? (
        <Row label="Text" align="start">
          <textarea className={`${textareaClass} min-h-[110px] max-w-[560px]`} value={tpl.body || ""} onChange={(e) => onPatch({ body: e.target.value })} aria-label={`${tpl.label} text`} />
          {error && <div className="mt-1 text-[12.5px] text-[#D92D20]">{error}</div>}
        </Row>
      ) : (
        <TemplatePick
          templates={templates}
          fields={fields}
          pick={pick}
          onPick={(p) => {
            onPick(p);
            const t = templates.find((x) => x.id === p?.id);
            if (t?.chat360_name) onPatch({ template_name: t.chat360_name, send_mode: "template" });
          }}
          allowManual
          manualName={tpl.template_name || tpl.platform_default || ""}
          onManualName={(template_name) => onPatch({ template_name })}
        />
      )}
    </div>
  );
}

export { Row, Fixed, DelayInput, ACTIONS, ESCALATIONS, BOOKING_FILES, TEMPLATE_FILE, FILE_WORDS, DELIVERY_OPTIONS };
